import React, { useState } from 'react';

import { Path } from '~/paths';
import { ChapterWrapper } from '~/ChapterWrapper';
import Footer from '~/Footer';
import { ELink } from '~/ExternalLink';

import Plot from "react-plotly.js";
import { CollapsibleSection } from '~/CollapsibleSection';
import { MathJax } from 'better-react-mathjax';

const ci_sym1 = require('../assets/images/ci_sym1.png');
const ci_sym2 = require('../assets/images/ci_sym2.png');

export const IntervalEstimationMeta = {
    title: "Intervalové odhady",
    shortTitle: "Intervalové odhady",
    path: Path.interval_estimation,
    element: (sectionNumber:string) => <IntervalEstimation sectionNumber={sectionNumber}/>,
    sectionNumber: "",
}

const TOCSpec = [
    "ci-terminology",
    "ci-principle",
    "ci-mu-derivation",
    "ci-simulation",
    "ci-mu",
    "ci-var-derivation",
    "ci-var",
    "ci-notes",
];


// Helper: random normal
function randn(mu: number, sigma: number): number {
  const u = 1 - Math.random();
  const v = Math.random();
  return (
    mu + sigma * Math.sqrt(-2.0 * Math.log(u)) * Math.cos(2.0 * Math.PI * v)
  );
}

// Helper: sample mean
function avg(arr: number[]): number {
  return arr.reduce((a, b) => a + b, 0) / arr.length;
}

// Helper: sample std dev
function std(arr: number[]): number {
  const m = avg(arr);
  return Math.sqrt(
    arr.map((x) => (x - m) ** 2).reduce((a, b) => a + b, 0) / (arr.length - 1)
  );
}

// Approximate t critical value (df ≤ 30 only)
function tCriticalValue(df: number, alpha: number): number {
    const tTable: Record<number, number> = {
        1: 12.71,
        2: 4.303,
        3: 3.182,
        4: 2.776,
        5: 2.571,
        6: 2.447,
        7: 2.365,
        8: 2.306,
        9: 2.262,
        10: 2.228,
        11: 2.201,
        12: 2.179,
        13: 2.160,
        14: 2.145,
        15: 2.131,
        16: 2.120,
        17: 2.110,
        18: 2.101,
        19: 2.093,
        20: 2.086,
        21: 2.080,
        22: 2.074,
        23: 2.069,
        24: 2.064,
        25: 2.060,
        26: 2.056,
        27: 2.052,
        28: 2.048,
        29: 2.045,
        30: 2.042,
    };
    return tTable[df] || 2.0; // fallback approx // limit 1.960
}

type SampleInfo = {
    sample: number[];
    mean: number;
    sd: number;
}

type CI = {
    x0: number;
    x1: number;
    y: number;
    containsTrueMean: boolean;
    method: "normal" | "t";
    sampleInfo: SampleInfo;
    refs: CI[];
};

type CovStat = {
    method: string;
    coverage: number;
    covered: number;
    nSim: number;
};

function simulateCIs(
    nSim: number,
    sampleSize: number,
    mu: number,
    sigma: number,
    alpha: number
): {
    normalCIs: CI[];
    tCIs: CI[];
    coverageStats: CovStat[];
} {
    const normalCIs: CI[] = [];
    const tCIs: CI[] = [];
    let normalCovered = 0;
    let tCovered = 0;

    for (let i = 0; i < nSim; i++) {
        const sample: number[] = Array.from({ length: sampleSize }, () =>
            randn(mu, sigma)
        );
        const mean = avg(sample);
        const sd = std(sample);
        const sem = sd / Math.sqrt(sampleSize);

        // Normal CI
        const z = 1.96; // 95%
        const ciNorm = [mean - z * sem, mean + z * sem];
        const normContains = mu >= ciNorm[0] && mu <= ciNorm[1];
        if (normContains) normalCovered++;

        const sampleInfo: SampleInfo = {
            sample: sample,
            mean: mean,
            sd: sd,
        };

        const nCI: CI = {
            x0: ciNorm[0],
            x1: ciNorm[1],
            y: i * 0.1,
            containsTrueMean: normContains,
            method: "normal",
            sampleInfo: sampleInfo,
            refs: [],
        }


        // t CI
        const t = tCriticalValue(sampleSize - 1, alpha / 2);
        const ciT = [mean - t * sem, mean + t * sem];
        const tContains = mu >= ciT[0] && mu <= ciT[1];
        if (tContains) tCovered++;

        const tCI: CI = {
            x0: ciT[0],
            x1: ciT[1],
            y: i * 0.1,
            containsTrueMean: tContains,
            method: "t",
            sampleInfo: sampleInfo,
            refs: [],
        };

        nCI.refs = [nCI, tCI];
        tCI.refs = [nCI, tCI];

        normalCIs.push(nCI);
        tCIs.push(tCI);
    }

    const coverageStats = [
        { method: "Normální", coverage: (normalCovered / nSim) * 100, covered: normalCovered, nSim: nSim },
        { method: "Studentovo t", coverage: (tCovered / nSim) * 100, covered: tCovered, nSim: nSim },
    ];

    return { normalCIs, tCIs, coverageStats };
}


const NSIM = 100;

function getHoverText(ci: CI): string {
    // join all sample values with commas
    let sampleText = ci.sampleInfo.sample.map((x) => x.toFixed(1)).join(", ");
    sampleText += `<br>Výb. průměr: ${ci.sampleInfo.mean.toFixed(2)}`;
    sampleText += `<br>Výb. sm. odch: ${ci.sampleInfo.sd.toFixed(2)}`;
    sampleText += `<br>Interval: [${ci.x0.toFixed(2)}, ${ci.x1.toFixed(2)}]`;
    return sampleText;
}

function generateCITraces(ciList: CI[], methodColor: string) {
    return ciList.map((ci) => ({
        //type: "scatter",
        mode: "lines",
        name: ci.method === "t" ? "Studentovo t" : "Normální",
        x: [ci.x0, ci.x1],
        y: [ci.y, ci.y],
        line: {
        color: ci.containsTrueMean ? methodColor : "red",
        width: ci.method === "t" ? 2 : 5,
        },
        hovertemplate: getHoverText(ci),

        showlegend: false, // avoid legend clutter
    }));
};

function generateDots(ciList: CI[]) {
    return ciList.map((ci) => ({
        //type: "scatter", // must be explicitly set
        mode: "markers", // this disables connecting lines
        name: "dots",
        x: ci.sampleInfo.sample.map((x) => x).sort((a, b) => a - b), // sort sample values
        y: ci.sampleInfo.sample.map((x) => ci.y),
        marker: {
            size: 2,
            color: "magenta",
            symbol: "cross",
        },
        hoverinfo: "skip",
        showlegend: false, // avoid legend clutter
    }));
};

function normalDensityTrace(mu: number, sigma: number) {
    const x: number[] = [];
    const y: number[] = [];
    for (let v = 120; v <= 230; v += 0.5) {
        const density =
        (1 / (sigma * Math.sqrt(2 * Math.PI))) *
        Math.exp(-((v - mu) ** 2) / (2 * sigma ** 2));
        x.push(v);
        y.push(density * 15); // scale for display, adjust as needed
    }

    return {
        //type: "scatter",
        mode: "lines",
        name: "Normal density (scaled)",
        x,
        y,
        line: {
            color: "grey",
            width: 1,
            //dash: "dashdot",
        },
        hoverinfo: "skip",
        fill: "tozeroy",
        fillcolor: "rgba(200, 200, 200, 0.4)", // light gray with transparency
    };
}

function CISimulationPlot(){
    const trueMean = 175;
    //const nSim = 100;
    const sampleSize = 5;
    const sigma = 10;
    const alpha = 0.05;

    const [simulation, setSimulation] = useState(() =>
        simulateCIs(NSIM, sampleSize, trueMean, sigma, alpha)
    );

    const { normalCIs, tCIs, coverageStats } = simulation;

    const regenerate = () => {
        setSimulation(simulateCIs(NSIM, sampleSize, trueMean, sigma, alpha));
    };

    return (
    <div style={{display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center'}}>
        <p>
        Náhodný výběr z normálního rozdělení s {"$\\mu = 175$"} a {"$\\sigma = 10$"} rozsahu {"$n = 5$"}.
        Intervalový odhad střední hodnoty {"$\\mu$"} pomocí normálního rozdělení a Studentova {"$t$"}-rozdělení,
        s {"$\\alpha = 0.05$"} (95% spolehlivost). Sto opakování (jedno opakování = jeden náhodný výběr velikosti {"$5$"},
        a z něj určený interval).
        </p>
        <Plot
            data={[
                normalDensityTrace(trueMean, sigma),
                ...generateCITraces(normalCIs, "#AABBEE"),
                ...generateCITraces(tCIs, "green"),
                ...generateDots(normalCIs),
                {
                type: "scatter",
                mode: "lines",
                name: "True mean",
                x: [trueMean, trueMean],
                y: [0, Math.max(normalCIs.length, tCIs.length) * 0.1],
                line: { color: "black", dash: "dot", width: 2 },
                },
            ]}
            layout={{
                xaxis: { range: [120, 230] },
                yaxis: { showticklabels: false },
                legend: { orientation: "h" },
                showlegend: false,
                height: 1000,
                width: 700,
                margin: { t: 0, r: 0, b: 20, l: 10 },
            }}
        />
        <div style={{ marginTop: "1rem", display: "flex", gap: "2rem", fontSize: "0.75rem" }}>
            <div style={{ display: "flex", alignItems: "center", gap: "0.1rem" }}>
                <div style={{ width: "20px", height: "4px", backgroundColor: "green" }} />
                <span>Studentovo t (CI pokrývá μ)</span>
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: "0.1rem" }}>
                <div style={{ width: "20px", height: "4px", backgroundColor: "#AABBEE" }} />
                <span>Normální (CI pokrývá μ)</span>
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: "0.1rem" }}>
                <div style={{ width: "20px", height: "4px", backgroundColor: "red" }} />
                <span>CI mine μ (obě metody)</span>
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: "0.1rem" }}>
                <div style={{ width: "2px", height: "16px", backgroundColor: "black", borderLeft: "1px dotted black" }} />
                <span>Skutečné μ</span>
            </div>
        </div>
        <table style={{ marginTop: "1rem", borderCollapse: "collapse" }}>
            <thead>
                <tr>
                    <th style={{ borderBottom: "1px solid #ccc", padding: "4px" }}>
                    Metoda
                    </th>
                    <th style={{ borderBottom: "1px solid #ccc", padding: "4px" }}>
                    CI mine μ
                    </th>
                    <th style={{ borderBottom: "1px solid #ccc", padding: "4px" }}>
                    CI pokrývá μ
                    </th>
                    <th style={{ borderBottom: "1px solid #ccc", padding: "4px" }}>
                    Pokrytí (%)
                    </th>
                </tr>
            </thead>
            <tbody>
                {coverageStats.map((row) => (
                    <tr key={row.method}>
                    <td style={{ padding: "4px" }}>{row.method}</td>
                    <td style={{ padding: "4px", color: "red" }}>{row.nSim - row.covered}/{row.nSim}</td>
                    <td style={{ padding: "4px", color: `${row.method === 'Normální' ? "#556699" : "green" }`}}>{row.covered}/{row.nSim}</td>
                    <td style={{ padding: "4px" }}>{row.coverage.toFixed(1)}%</td>
                    </tr>
                ))}
            </tbody>
        </table>
        <div style={{ marginTop: "1rem" }}>
            <button
            onClick={regenerate}
            style={{
                padding: "8px 16px",
                fontSize: "1rem",
                cursor: "pointer",
                backgroundColor: "#007bff",
                color: "white",
                border: "none",
                borderRadius: "4px",
            }}
            >
            Přegenerovat
            </button>
        </div>
    </div>
    );
}


export function IntervalEstimation({sectionNumber}: {sectionNumber: string}) {

    return (
        <ChapterWrapper sectionNumber={sectionNumber} title={IntervalEstimationMeta.title} TOCSpec={TOCSpec}>

            <p>
            Viz přednášky (poznámky, videa), a nebo např.
            &nbsp;<ELink href={"https://www.youtube.com/watch?v=EJe3jiZNwUU"}>youtube 1</ELink>,
            &nbsp;<ELink href={"https://www.youtube.com/watch?v=9GtaIHFuEZU"}>youtube 2</ELink>,
            &nbsp;<ELink href={"https://www.youtube.com/watch?v=BQ88ni4bJNA"}>youtube 3</ELink>,
            <br/>nebo
            &nbsp;<ELink href={"https://is.muni.cz/do/rect/el/estud/prif/ps13/prav_stat/web_2/pages/04-inerval-odhady.html"}>kurz MUNI</ELink>
            &nbsp;(matematičtější),
            nebo
            &nbsp;<ELink href={"https://www.stat.berkeley.edu/~stark/SticiGui/Text/confidenceIntervals.htm"}>Stark</ELink>.
            </p>

            <h2 id="ci-terminology">Terminologie</h2>
            <table className='table-interval-estimation simple-table'>
                <tbody>
                    <tr>
                        <td>{"$\\alpha$"}</td>
                        <td><i>hladina významnosti</i></td>
                        <td>typicky {"$\\alpha = 0.05 \\ (5\\%)$"}, nebo {"$\\alpha = 0.01 \\ (1\\%)$"}</td>
                    </tr>
                    <tr>
                        <td>{"$1 - \\alpha$"}</td>
                        <td><i>spolehlivost</i></td>
                        <td>typicky {"$1 - \\alpha = 0.95 \\ (95\\%)$"}, nebo {"$1 - \\alpha = 0.99 \\ (99\\%)$"}</td>
                    </tr>
                </tbody>
            </table>

            <p>
            Výsledkem intervalového odhadu na konkrétních datech je <strong>interval spolehlivosti</strong>,
            aka. <strong>konfidenční interval</strong> (anglická zkratka <strong>CI</strong>, z <i>confidence interval</i>).
            </p>

            <h2 id="ci-principle">Princip</h2>

            <p>
            Intervalový odhad můžeme zkonstruovat pro různé parametry nebo odvozené číselné charakteristiky rozdělení,
            např. střední hodnotu {"$\\mu$"}, rozptyl {"$\\sigma^2$"}, medián, nějaký kvantil, šikmost, atp.
            </p>

            <p>
            Ať už odhadujeme jakýkoli parametr {"$\\theta$"} (myslete si např. {"$\\theta = \\mu$"} nebo {"$\\theta = \\sigma^2$"}),
            princip intervalového odhadu je následující: Z náhodného výběru {"$X_1, X_2, \\ldots, X_n$"} vypočítáme
            nějakou statistiku {"$Y(X_1, X_2, \\ldots, X_n)$"}, jejíž hodnoty jsou s velkou pravděpodobností
            koncentrované kolem skutečné hodnoty parametru {"$\\theta$"}. Tato statistika je "ušitá na míru" podle toho,
            co odhadujeme -  např. pro {"$\\mu$"} bude založena na výběrovém průměru {"$\\bar{X}$"} a pro {"$\\sigma^2$"} bude
            založena na výběrovém rozptylu {"$S^2$"}. Když víme, jaké má statistika {"$Y$"} rozdělení, můžeme pomocí jeho kvantilů
            určit interval spolehlivosti.
            </p>

            <p>Interval získaný intervalovým odhadem je <strong>náhodná veličina</strong> (resp. dvojice náhodných veličin: horní mez, dolní mez),
            protože při různých realizacích {"$x_1, x_2, \\ldots, x_n$"} náhodného výběru {"$X_1, X_2, \\ldots, X_n$"} dostaneme různé
            polohy a délky tohoto intervalu. Zhruba řečeno: stejně tak jako ne každá realizace {"$x_1, x_2, \\ldots, x_n$"} má stejnou
            pravděpodobnost, tak ne každý konfidenční interval má stejnou pravděpodobnost, že bude výsledkem. Konfidenční interval
            má svoje rozdělení pravděpodobnosti (!) /je to náhodná veličina/. V některých realizacích do něj paramter {"$\\theta$"} spadne,
            v jiných ne. Jde o to, aby pravděpodonost množiny těch případů, kdy do něj spadne, byla alespoň {"$1 - \\alpha$"}. Pak se na
            tento proces odhadu můžeme s touto pravděpodobností spolehnout - při opakovaných realizacích bude
            v maximálně {"$\\alpha * 100$"} procentech případů skutečná (ale neznámá) hodnota {"$\\theta$"} mimo interval získaný
            v dané realizaci (samozřejmě, aniž bychom
            věděli, že je to zrovna tento případ). Tento princip je ilustrován v sekci Simulace níže.
            </p>

            <CollapsibleSection
                title="Intervalový odhad střední hodnoty - odvození, logika věci"
                hid="ci-mu-derivation"
                initiallyOpen={false}
                content={[
                <>
                <MathJax dynamic inline>
                <p>
                Předpoklad: máme náhodný výběr z normálního rozdělení {"$N(\\mu, \\sigma^2)$"}, tj. každé {"$X_i \\sim N(\\mu, \\sigma^2)$"}
                </p>

                <p>
                Už víme, že v tomto případě má výběrový průměr {"$\\bar{X}$"} normální rozdělení {"$N(\\mu, \\frac{\\sigma^2}{n})$"}.
                Dále {"$q_p$"} bude značit p-kvantil tohoto rozdělení. Interval
                {`$$
                    I = \\langle q_{\\frac{\\alpha}{2}}, q_{1 - \\frac{\\alpha}{2}} \\rangle
                $$`}
                z definice odpovídá pravděpodobnosti {"$1 - \\alpha$"}: {"$P(\\bar{X} \\in I) = 1 - \\alpha$"}. Výběrový průměr {"$\\bar{X}$"} vyjde
                s pravděpodobností {"$1 - \\alpha$"} v intervalu {"$I$"}. Protože rozdělení {"$N(\\mu, \\frac{\\sigma^2}{n})$"} je symetrické kolem {"$\\mu$"},
                platí
                {`\\begin{align}
                    \\langle q_{\\frac{\\alpha}{2}}, q_{1 - \\frac{\\alpha}{2}} \\rangle = \\langle \\mu - \\Delta, \\mu + \\Delta \\rangle
                \\end{align}`}
                kde přesnou hodnotu {"$\\Delta$"} určíme později.
                </p>

                <p>Výběrový průměr {"$\\bar{X}$"} padne s pravděpodobností {"$1 - \\alpha$"} do
                vzdálenosti {"$\\pm \\Delta$"} od skutečné střední hodnoty {"$\\mu$"}; a jen s
                pravděpodobností {"$\\alpha$"} padne dál (!).
                </p>

                <p>Následující pozorování je nejspíš úplně zřejmé, ale snad neuškodí si ho uvést explicitně.
                Když máme dvě čísla {"$a$"} a {"$b$"}, pak číslo {"$a$"} je k číslu {"$b$"} blíže
                než {"$\\Delta$"} právě tehdy, když je číslo {"$b$"} k číslu {"$a$"} blíže než {"$\\Delta$"} (symetrie):
                </p>

                <div style={{textAlign: "center", paddingBottom: 10}}>
                    <img src={ci_sym1} style={{width: "40%"}}/>
                </div>

                <p>
                Když tuto úvahu aplikujeme na {"$a = \\mu$"} a {"$b = \\bar{X}$"}, dostáváme:
                </p>

                <div style={{textAlign: "center", paddingBottom: 10}}>
                    <img src={ci_sym2} style={{width: "40%"}}/>
                </div>

                <p>
                neboli: "Skutečná střední hodnota {"$\\mu$"} je od výběrového průměru {"$\\bar{X}$"} vzdálena
                nejvýše o {"$\\Delta$"} s pravděpodobností {"$1 - \\alpha$"}." Toto už je hledaný intervalový odhad se spolehlivostí {"$1 - \\alpha$"}:
                {`$$
                    \\mu \\in \\left\\langle \\bar{x} - \\Delta, \\bar{x} + \\Delta \\right\\rangle
                $$`}
                Zbývá už jen určit hodnotu {"$\\Delta$"}. K tomu potřebujeme kvantily normálního rozdělení {"$N(\\mu, \\frac{\\sigma^2}{n})$"};
                standardně (tabulka) se pracuje s kvantily {"$u_p$"} standardního normálního rozdělení {"$N(0,1)$"}, takže je potřeba je převést.
                </p>

                <p>
                Kvantily se tranformují stejně jako náhodná veličina: Je-li {"$Y = aX + b$"}, pak {"$y_p = a x_p + b$"}. Je-li totiž {"$x_p$"} p-kvantil veličiny {"$X$"}, pak
                {`$$
                    p = P(X \\leq x_p) = P\\left(\\frac{Y - b}{a} \\leq x_p\\right) = P(Y - b \\leq a x_p) = P(Y \\leq a x_p + b),
                $$`}
                tedy číslo {"$y_p = a x_p + b$"} je z definice p-kvantil veličiny {"$Y$"}.
                </p>

                <p>Protože rozdělení {"$N(\\mu, \\frac{\\sigma^2}{n})$"} je rozdělení náhodné veličiny {"$Y = \\mu + \\frac{\\sigma}{\\sqrt{n}} U$"} ({"$U \\sim N(0,1)$"}), máme
                {`$$
                    q_p = \\mu + \\frac{\\sigma}{\\sqrt{n}} u_p,
                $$`}
                Pak z rovnice (1) výše vyjádříme {"$\\Delta$"}:
                {`\\begin{align*}
                    \\Delta & = q_{1 - \\alpha/2} - \\mu = \\frac{\\sigma}{\\sqrt{n}} u_{1 - \\alpha/2}, \\textrm{popř.} \\\\
                    \\Delta & = \\mu - q_{\\alpha/2} = - \\frac{\\sigma}{\\sqrt{n}} u_{\\alpha/2} \\\\
                \\end{align*}`}
                Vzhledem k symetrii normálního rozdělení platí {"$u_{\\alpha/2} = - u_{1 - \\alpha/2}$"}, takže obě rovnice
                dávají stejnou hodnotu {"$\\Delta$"}.
                </p>

                <p>
                Získali jsme intervalový odhad:
                {`$$
                    \\mu \\in \\left\\langle \\bar{x} - u_{1 - \\alpha/2} \\frac{\\sigma}{\\sqrt{n}}, \\bar{x} + u_{1 - \\alpha/2} \\frac{\\sigma}{\\sqrt{n}}\\right\\rangle
                $$`}
                </p>

                <p>
                Tento intervalový odhad je použitelný <b>při známém rozptylu</b> {"$\\sigma^2$"} (musíme být schopni dosadit známou
                hodnotu {"$\\sigma$"} do vzorce) - což se v praxi nestane.
                </p>

                <p>
                Při <b>neznámém rozptylu</b> {"$\\sigma^2$"} se nabízí "tam místo {"$\\sigma$"} šupnout výběrový rozptyl {"$S$"}", jakožto odhad {"$\\sigma$"}.
                Protože se však jedná o náhodnou veličinu, nikoli konstantu, v principu se změní rozdělení, jehož kvantily používáme pro odhad.
                Toho si primárně všimnul pan Gosset (alias Student), a podařilo se mu odvodit, jaké rozdělení se tam objeví (jmenuje se po něm Studentovo {"$t$"}-rozdělení).
                </p>

                <p>V tomto odvození už je potřeba postupovat o něco méně intuitivně. Matematicky se odvodí, že
                náhodná veličina
                {`$$
                    T = \\frac{\\bar{X} - \\mu}{\\frac{S}{\\sqrt{n}}}
                $$`}
                má Studentovo {"$t$"}-rozdělení s {"$n - 1$"} stupni volnosti - viz kapitola o Studentově rozdělení.
                Pak tedy platí, že
                {`$$
                    P\\left(t_{\\alpha/2}(n-1) \\leq \\frac{\\bar{X} - \\mu}{\\frac{S}{\\sqrt{n}}} \\leq t_{1 - \\alpha/2}(n-1)\\right) = 1 - \\alpha
                $$`}
                Upravíme nerovnosti:
                {`\\begin{array}{rcccl}
                    t_{\\alpha/2}(n-1) &\\leq& \\frac{\\bar{X} - \\mu}{\\frac{S}{\\sqrt{n}}} &\\leq& t_{1 - \\alpha/2}(n-1) \\quad / \\cdot \\frac{S}{\\sqrt{n}} \\\\
                    t_{\\alpha/2}(n-1) \\frac{S}{\\sqrt{n}} &\\leq& \\bar{X} - \\mu &\\leq& t_{1 - \\alpha/2}(n-1) \\frac{S}{\\sqrt{n}} \\quad / \\cdot (-1) \\\\
                    - t_{\\alpha/2}(n-1) \\frac{S}{\\sqrt{n}} &\\geq& \\mu - \\bar{X} &\\geq& - t_{1 - \\alpha/2}(n-1) \\frac{S}{\\sqrt{n}} \\quad / \\textrm{otočíme} \\\\
                    - t_{1 - \\alpha/2}(n-1) \\frac{S}{\\sqrt{n}} &\\leq& \\mu - \\bar{X} &\\leq& - t_{\\alpha/2}(n-1) \\frac{S}{\\sqrt{n}} \\quad / \\textrm{symetrie kvantilů}(^*) \\\\
                    - t_{1 - \\alpha/2}(n-1) \\frac{S}{\\sqrt{n}} &\\leq& \\mu - \\bar{X} &\\leq& t_{1 - \\alpha/2}(n-1) \\frac{S}{\\sqrt{n}}  \\quad / + \\bar{X} \\\\
                    \\bar{X} - t_{1 - \\alpha/2}(n-1) \\frac{S}{\\sqrt{n}} &\\leq& \\mu &\\leq& \\bar{X} + t_{1 - \\alpha/2}(n-1) \\frac{S}{\\sqrt{n}}
                \\end{array}`}
                {"$(^*)$"} Studentovo rozdělení je symetrické, takže {"$t_{\\alpha/2}(n-1) = - t_{1 - \\alpha/2}(n-1)$"}.
                </p>
                <p>Získáme
                {`$$
                    P\\left(\\bar{X} - t_{1 - \\alpha/2}(n-1) \\frac{S}{\\sqrt{n}} \\leq \\mu \\leq \\bar{X} + t_{1 - \\alpha/2}(n-1) \\frac{S}{\\sqrt{n}}\\right) = 1 - \\alpha
                $$`}
                takže hledaný intervalový odhad střední hodnoty {"$\\mu$"} je
                {`$$
                    \\mu \\in \\left\\langle \\bar{x} - t_{1 - \\alpha/2}(n-1) \\frac{s}{\\sqrt{n}}, \\bar{x} + t_{1 - \\alpha/2}(n-1) \\frac{s}{\\sqrt{n}}\\right\\rangle
                $$`}
                </p>

                </MathJax>
                </>
            ]}
            />

            <h2 id="ci-mu">Intervalový odhad střední hodnoty</h2>
            <p>
            Intervalový odhad střední hodnoty rozdělení {"$N(\\mu, \\sigma^2)$"} při  <strong>známém{"$^*$"} rozptylu</strong> {"${\\textcolor{RoyalBlue}{\\sigma}}^2$"} je:
            {`$$
                \\mu \\in \\left\\langle \\bar{x} - {\\textcolor{Rhodamine}{u_{1 - \\alpha/2}}} \\frac{{\\textcolor{RoyalBlue}{\\sigma}}}{\\sqrt{n}}, \\bar{x} + {\\textcolor{Rhodamine}{u_{1 - \\alpha/2}}} \\frac{{\\textcolor{RoyalBlue}{\\sigma}}}{\\sqrt{n}}\\right\\rangle
            $$`}
            kde {"$\\bar{x}$"} je hodnota výběrového průměru, {"${\\textcolor{Rhodamine}{u_{1 - \\alpha/2}}}$"} je
            kvantil <span style={{color: "#FC58AA"}}>normálního rozdělení {"${\\textcolor{Rhodamine}{N(0,1)}}$"} </span> a {"$n$"} je velikost výběru. <br/>
            {"$^*$"}V praxi většinou rozptyl {"$\\sigma^2$"} neznáme, takže tento odhad má velmi omezené (až nulové) praktické využití.
            Teoreticky je ale hezký v tom, že umožňuje "přímé" odvození - viz sekce výše, a umožňuje nám tak budovat intuici.
            </p>

            <p>
            Intervalový odhad střední hodnoty rozdělení {"$N(\\mu, \\sigma^2)$"} při <strong>neznámém rozptylu</strong> {"$\\sigma^2$"} - pomocí Studentova {"$t$"}-rozdělení:
            {`$$
                \\mu \\in \\left\\langle \\bar{x} - {\\textcolor{green}{t_{1-\\alpha/2}(n-1)}} \\frac{{\\textcolor{RoyalBlue}{s}}}{\\sqrt{n}}, \\bar{x} + {\\textcolor{green}{t_{1-\\alpha/2}(n-1)}}  \\frac{{\\textcolor{RoyalBlue}{s}}}{\\sqrt{n}}\\right\\rangle
            $$`}
            kde {"$\\bar{x}$"} je hodnota výběrového průměru, {"${\\textcolor{green}{t_{1-\\alpha/2}(n-1)}}$"} je
            kvantil <span style={{color: "green"}}>Studentova {"$t$"}-rozdělení</span> (pro {"$\\alpha = 0.05$"} a {"$n-1$"} stupňů
            volnosti), {"${\\textcolor{RoyalBlue}{s}}$"} je hodnota výběrového rozptylu a {"$n$"} je velikost náhodného výběru.
            </p>

            <h2 id="ci-simulation">Simulace</h2>

            <p>
            Simulace níže ukazuje, co se stane, když při neznámem rozptylu (tj. počítáme se {"$s$"} místo {"$\\sigma$"}) použijeme (nesprávně) kvantily normálního rozdělení
            místo Studentova {"$t$"}-rozdělení: Intervaly takto zkonstruované jsou užší, takže skutečnou střední hodnotu pokryjí v méně než 95% případů! (Viz tabulka dole.)
            </p>

            <CISimulationPlot/>


            <CollapsibleSection
                title="Intervalový odhad rozptylu - odvození"
                hid="ci-var-derivation"
                initiallyOpen={false}
                content={[
                <>
                <MathJax dynamic inline>
                <p>
                Předpoklad: máme náhodný výběr z normálního rozdělení {"$N(\\mu, \\sigma^2)$"}, tj. každé {"$X_i \\sim N(\\mu, \\sigma^2)$"}
                </p>
                <p>Matematicky se odvodí, že náhodná veličina
                {`$$
                    K = \\frac{(n-1)S^2}{\\sigma^2}
                $$`}
                má {"$\\chi^2$"}-rozdělení s {"$n - 1$"} stupni volnosti. Pak platí, že
                {`$$
                    P\\left(\\chi^2_{\\alpha/2}(n-1) \\leq \\frac{(n-1)S^2}{\\sigma^2} \\leq \\chi^2_{1 - \\alpha/2}(n-1)\\right) = 1 - \\alpha
                $$`}
                Upravíme nerovnosti:
                {`\\begin{array}{rcccl}
                    \\chi^2_{\\alpha/2}(n-1) &\\leq& \\frac{(n-1)S^2}{\\sigma^2} &\\leq& \\chi^2_{1 - \\alpha/2}(n-1) \\quad / \\frac{1}{.} (^*)\\\\
                    \\frac{1}{\\chi^2_{\\alpha/2}(n-1)} &\\geq& \\frac{\\sigma^2}{(n-1)S^2} &\\geq& \\frac{1}{\\chi^2_{1 - \\alpha/2}(n-1)} \\quad / \\textrm{otočíme} \\\\
                    \\frac{1}{\\chi^2_{1 - \\alpha/2}(n-1)} &\\leq& \\frac{\\sigma^2}{(n-1)S^2} &\\leq& \\frac{1}{\\chi^2_{\\alpha/2}(n-1)} \\quad / \\cdot (n-1)S^2 \\\\
                    \\frac{(n-1)S^2}{\\chi^2_{1 - \\alpha/2}(n-1)} &\\leq& \\sigma^2 &\\leq& \\frac{(n-1)S^2}{\\chi^2_{\\alpha/2}(n-1)}
                \\end{array}`}
                {"$(^*)$"} Převrácená hodnota: je-li {"$a \\leq b$"}, pak {"$\\frac{1}{a} \\geq \\frac{1}{b}$"} (pro {"$a, b > 0$"}).
                </p>
                <p>Získali jsme intervalový odhad pro rozptyl:
                {`$$
                    \\sigma^2 \\in \\left\\langle \\frac{(n-1)s^2}{\\chi^2_{1 - \\alpha/2}(n-1)}, \\frac{(n-1)s^2}{\\chi^2_{\\alpha/2}(n-1)}\\right\\rangle
                $$`}
                </p>
                </MathJax>
                </>
                ]}
            />
            <h2 id="ci-var">Intervalový odhad rozptylu</h2>
            <p>
            Intervalový odhad rozptylu {"$\\sigma^2$"} normálního rozdělení {"$N(\\mu, \\sigma^2)$"}:
            {`$$
                \\sigma^2 \\in \\left\\langle \\frac{(n-1)s^2}{\\chi^2_{1 - \\alpha/2}(n-1)}, \\frac{(n-1)s^2}{\\chi^2_{\\alpha/2}(n-1)}\\right\\rangle,
            $$`}
            kde {"$s^2$"} je hodnota výběrového rozptylu, {"$\\chi^2_{p}(n-1)$"} jsou
            p-kvantily {"$\\chi^2$"}-rozdělení s {"$n - 1$"} stupni volnosti a {"$n$"} je velikost náhodného výběru.
            </p>

            <h2 id="ci-notes">Poznámky</h2>
            <p>
            Zde jsme si ukázali intervalové odhady parametrů normálního rozdělení {"$N(\\mu, \\sigma^2)$"}.
            Intervalové odhady pro jiná rozdělení (např. exponenciální, binomické, Poissonovo, atd.)
            mají jiné vzorce, ale princip konstrukce/odvození (a toho, jak je interpretovat a pouížívat) je stejný.
            </p>
            <Footer/>
        </ChapterWrapper>
    );
}

